import { QueryClient, queryOptions, useQueryClient } from '@tanstack/react-query';
import { Study } from 'app/interfaces/Study';
import { StudyStore } from 'app/interfaces/StudyStore';
import StudyStoreContext from 'app/providers/StudyStoreProvider/context';
import { GetStudyParams } from 'app/queries/getStudyQueryOptions';
import createDeferredPromise from 'app/utils/createDeferredPromise';
import _ from 'lodash';
import { useContext } from 'react';

type Dependencies = {
  studyStore: StudyStore;
  queryClient: QueryClient;
};

export default function getStudiesQueryOptions<Options>(
  params: GetStudyParams[],
  dependencies?: Dependencies,
  options?: Options
) {
  const queryClient = dependencies?.queryClient ?? useQueryClient();
  const studyStore = dependencies?.studyStore ?? useContext(StudyStoreContext);

  return queryOptions({
    ...options,
    queryKey: [{ query: '/studies' }, params] as const,
    queryFn: async ({ queryKey }) => {
      const [, params] = queryKey;

      const availableData: (Study | null)[] = [];
      const pendingStudyIDPromises: Record<string, DeferredPromise<Study | null>> = {};
      const pendingStudyInstanceUIDPromises: Record<string, DeferredPromise<Study | null>> = {};

      const pendingPromises = _.filter(
        params.map((param) => {
          // If cached data already exists, we use it without refetching.
          const data = queryClient.getQueryData<Study | null>([
            { query: '/study' },
            param,
          ] as const);
          if (data !== undefined) {
            availableData.push(data);
            return undefined;
          }

          // If no cached data, we create prefetching.
          const promise = createDeferredPromise();
          queryClient.prefetchQuery({
            ...options,
            queryKey: [{ query: '/study' }, param] as const,
            queryFn: () => promise,
          });
          return [param, promise] as const;
        })
      );

      try {
        const pendingStudyIDs = _.filter(pendingPromises.map(([param]) => param?.studyID));
        const studiesByID =
          pendingStudyIDs.length > 0 ? await studyStore.getStudies(pendingStudyIDs) : [];

        _.forEach(studiesByID, (study) => {
          const foundIndex = _.findIndex(
            pendingPromises,
            ([param]) => param?.studyID === study._id
          );
          if (foundIndex === -1) return;
          const [, promise] = pendingPromises[foundIndex];
          promise.resolve(study);
          pendingPromises.splice(foundIndex, 1);
          availableData.push(study);
        });

        // Remaining stuides are searched with studyInstanceUID, if some were not found by sutdyID, we search by studyInstanceUID.

        const pendingStudyInstanceUIDs = _.filter(
          pendingPromises.map(([param]) => param?.studyInstanceUID)
        );

        const studiesByInstanceUID =
          pendingStudyInstanceUIDs.length > 0
            ? await studyStore.filterStudies({
                studyInstanceUIDs: pendingStudyInstanceUIDs,
              })
            : [];

        _.forEach(studiesByInstanceUID, (study) => {
          const foundIndex = _.findIndex(
            pendingPromises,
            ([param]) => param?.studyInstanceUID === study.study_instance_uid
          );
          if (foundIndex === -1) return;
          const [, promise] = pendingPromises[foundIndex];
          promise.resolve(study);
          pendingPromises.splice(foundIndex, 1);
          availableData.push(study);
        });

        // Remaining studies were not found we resolve to null.
        _.forEach(pendingPromises, ([param, promise]) => {
          promise.resolve(null);
        });
      } catch (error) {
        // If an error occurs, we reject all pending promises.
        _.forEach(pendingStudyIDPromises, (promise) => promise.reject(error));
        _.forEach(pendingStudyInstanceUIDPromises, (promise) => promise.reject(error));

        throw error;
      }

      return Promise.all(availableData);
    },
  });
}
